<script setup>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import GlobalHeader from './components/GlobalHeader.vue';
import GlobalFooter from './components/GlobalFooter.vue';
import TripFinder from './components/TripFinder.vue';
import BudgetEstimator from './components/BudgetEstimator.vue';
import ToastContainer from './components/ui/ToastContainer.vue';
import LoadingOverlay from './components/ui/LoadingOverlay.vue';
import GlobalHeaderMobile from './components/GlobalHeaderMobile.vue';
import AuthModal from './components/auth/AuthModal.vue';
import AccountLayout from './layouts/AccountLayout.vue';
import { useAppWindowEvents } from './composables/useAppWindowEvents.js';
import { useTripSearch } from './composables/useTripSearch.js';
import { useTrendingAuthFlow } from './composables/useTrendingAuthFlow.js';
import { toastManager } from './composables/useToasts.js';
import { useAuthStore } from './store/auth';
import { useSearchResultsStore } from './store/searchResults';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const searchResultsStore = useSearchResultsStore();
const { success, error, warning } = toastManager;

// Component map:
// - App.vue: app shell composition and feature wiring.
// - useTripSearch: transforms TripFinder payloads and executes streaming search workflow.
// - useTrendingAuthFlow: owns trending-card auth gating, pending actions, and destination navigation.
// - useAppWindowEvents: owns global window event registration and event-detail normalization.

// ── Trip Finder Modal State ──────────────────────────────────────────────
const showTripFinderModal = ref(false);
const showBudgetEstimatorModal = ref(false);
const showTripFinderSection = ref(false);

// Handle opening trip finder modal
const openTripFinderModal = () => {
  showTripFinderModal.value = true;
  // Ensure Budget Estimator is closed
  showBudgetEstimatorModal.value = false;
};

const closeTripFinderModal = () => {
  showTripFinderModal.value = false;
};

const {
  handleTripSearch,
  isTripFinderSearching,
  showLoadingOverlay,
  loadingMessage,
  loadingSubMessage,
} = useTripSearch({
  router,
  closeTripFinderModal,
  searchResultsStore,
});

const {
  showAuthModal,
  authModalMode,
  hasPendingAction,
  openAuthModal,
  handleOpenAuthModalEvent,
  closeAuthModal,
  handleTrendingCardSearch,
} = useTrendingAuthFlow({
  router,
  authStore,
  warning,
  success,
  error,
  loadingState: {
    isTripFinderSearching,
    showLoadingOverlay,
    loadingMessage,
    loadingSubMessage,
  },
});

// Handle Budget Estimator modal
const openBudgetEstimatorModal = () => {
  showBudgetEstimatorModal.value = true;
  // Ensure Trip Finder is closed
  showTripFinderModal.value = false;
};

const closeBudgetEstimatorModal = () => {
  showBudgetEstimatorModal.value = false;
};

// Handle closing trip finder (both modal and section)
const closeTripFinder = () => {
  showTripFinderModal.value = false;
  showTripFinderSection.value = false;
};

// Handle toggling trip finder section on desktop
const toggleTripFinderSection = () => {
  showTripFinderSection.value = !showTripFinderSection.value;
};

// Handle opening trip finder section on desktop
const openTripFinderSection = () => {
  if (import.meta.env.DEV) console.debug('openTripFinderSection called in App.vue', {
    currentValue: showTripFinderSection.value,
    route: router.currentRoute.value.path
  });
  showTripFinderSection.value = true;
};

// Handle navigation from the GlobalHeader component
const handleHeaderNavigation = (navData) => {
  if (navData && navData.page) {
    router.push(`/${navData.page}`);
  }
};

// Legacy navigation support for existing components
const navigateTo = (page) => {
  if (page === 'home') {
    router.push('/');
  } else {
    router.push(`/${page}`);
  }
};

// Listen for navigation events from child components (legacy support)
const handleAuthRedirectHome = () => {
  router.push('/');
};

// Auth bootstrap is owned by router.beforeEach — no onMounted initialize needed here.
// Router ensures initialize() runs once before the first render, preventing
// duplicate /users/me calls from App.vue + GlobalHeader + router all firing in parallel.

useAppWindowEvents({
  onNavigate: navigateTo,
  onNavigateToDetails: (destinationId) => {
    router.push({
      name: 'DestinationDetail',
      params: { slug: destinationId }
    });
  },
  onNavigateToActivities: (destinationId) => {
    router.push(`/activities/${destinationId}`);
  },
  onNavigateToMap: (destinationId) => {
    router.push(`/map/${destinationId}`);
  },
  onTrendingCardSearch: handleTrendingCardSearch,
  onOpenAuthModalEvent: handleOpenAuthModalEvent,
  onAuthRedirectHome: handleAuthRedirectHome,
});
</script>

<template>
  <div class="min-h-screen flex flex-col">
    <!-- Global Header with navigation event handling -->
    <GlobalHeader :showTripFinderModal="showTripFinderModal" :showTripFinderSection="showTripFinderSection"
      :showBudgetEstimatorModal="showBudgetEstimatorModal" @navigate="handleHeaderNavigation"
      @open-trip-finder="openTripFinderModal" @open-budget-estimator="openBudgetEstimatorModal"
      @toggle-trip-finder-section="toggleTripFinderSection" @open-trip-finder-section="openTripFinderSection"
      @close-trip-finder="closeTripFinder" @close-budget-estimator="closeBudgetEstimatorModal" />

    <!-- Trip Finder Modal (Both Desktop and Mobile) - Positioned after header to push content down -->
    <TripFinder :showModal="showTripFinderModal" :isSearching="isTripFinderSearching" @close="closeTripFinderModal"
      @search="handleTripSearch" />

    <!-- Budget Estimator Modal (Both Desktop and Mobile) - Positioned after Trip Finder -->
    <BudgetEstimator :showModal="showBudgetEstimatorModal" @close="closeBudgetEstimatorModal" />

    <main class="flex-grow pt-12 md:pt-14 pb-16 md:pb-0 transition-all duration-300" :class="{
      'md:pt-[166px]': (showTripFinderModal || showBudgetEstimatorModal) && route.name !== 'DestinationDetail',
      'md:!pt-0': route.name === 'DestinationDetail'
    }">
      <div class="relative overflow-hidden" :class="[
        route.meta?.noPageContainer || route.name === 'DestinationDetail' ? '' : 'page-container',
        route.name === 'Home' ? 'home-page-container' : ''
      ]">
        <!-- Router View for page content with global transition -->
        <!-- Use route params as key for destination details to force reload when slug changes -->
          <!-- Wrap with AccountLayout if route meta.layout is 'account' -->
          <template v-if="route.meta?.layout === 'account'">
            <AccountLayout>
              <router-view v-slot="{ Component }">
                <transition name="page">
                  <component :is="Component" :key="route.fullPath" />
                </transition>
              </router-view>
            </AccountLayout>
          </template>
          <template v-else>
            <router-view v-slot="{ Component }">
              <transition name="page">
                <keep-alive :include="['HomePage']">
                  <component :is="Component" :key="route.name === 'Home' ? 'Home' : route.fullPath" />
                </keep-alive>
              </transition>
            </router-view>
          </template>
      </div>
    </main>

    <GlobalFooter />

    <LoadingOverlay :show="showLoadingOverlay" :message="loadingMessage" :subMessage="loadingSubMessage" />
    <ToastContainer />
    <GlobalHeaderMobile @open-trip-finder="openTripFinderModal" @close-trip-finder="closeTripFinderModal"
      @open-auth-modal="openAuthModal" />
    <!-- AuthModal overlay is only shown when NOT on a dedicated auth route -->
    <AuthModal v-if="showAuthModal && !route.meta?.authModal" :show="showAuthModal"
      :initial-mode="authModalMode" :has-pending-action="hasPendingAction"
      @close="closeAuthModal" @auth-success="closeAuthModal" />
  </div>
</template>
